import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { DEFAULT_BUSINESS_TIME_ZONE, formatAppointmentDate } from "@zapis24/utils";
import { apiFetch, fmtPrice, fmtTime, toLocalDateStr } from "../api.js";
import {
  buildCompleteAppointmentPatch,
  getAppointmentActionAvailability,
  getMasterVisibleAppointmentStatusMeta,
} from "../appointmentsView.js";
import { ReturnReminderDaysPicker } from "../ReturnReminderDaysPicker.js";
import {
  getHomeAppointmentOpenTarget,
  getHomeDashboardDateLabel,
  getHomeOverdueAppointmentCardActions,
  getHomePrimaryAppointmentState,
  getHomePrimaryAppointmentTimeLabel,
  getHomeUrgentActionPresentation,
  getLaunchChecklistProgress,
  getLaunchChecklistStorageKey,
  getOtherTodayAppointments,
  getOtherTodayToggleLabel,
  getPlanAccessCard,
  LAUNCH_CHECKLIST_STEPS,
  type HomeAppointmentOpenTarget,
  type HomePrimaryAppointmentState,
  type HomePlanCardTone,
  type HomeSettingsSection,
  type HomeTargetTab,
  type LaunchChecklistStepId,
} from "../homeDashboard.js";

type AppointmentStatus =
  | "PENDING"
  | "CONFIRMED"
  | "IN_PROGRESS"
  | "CANCELLED"
  | "COMPLETED"
  | "NO_SHOW";
type PlanStatus = "FREE_LIMITED" | "ACTIVE" | "PAST_DUE" | "CANCELLED";
type UrgentPriority = "high" | "medium" | "low";

interface DashboardAppointment {
  id: string;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  notes: string | null;
  source: string;
  actualPrice: number | null;
  client: { id: string; name: string; phone: string | null };
  service: { id: string; name: string; durationMin: number; priceFrom?: number; priceTo?: number | null };
}

interface DashboardSlot {
  startsAt: string;
  endsAt: string;
  timeStr: string;
}

interface DashboardUrgentAction {
  id: string;
  priority: UrgentPriority;
  title: string;
  body: string;
  targetTab: HomeTargetTab;
}

interface MasterDashboard {
  generatedAt: string;
  business: { id: string; name: string; timeZone: string | null; planStatus: PlanStatus; planExpiresAt: string | null };
  today: {
    date: string;
    total: number;
    active: number;
    completed: number;
    revenueKopecks: number;
  };
  nextAppointment: DashboardAppointment | null;
  unfinishedAppointments: {
    total: number;
    items: DashboardAppointment[];
  };
  freeWindows: {
    service: { id: string; name: string; durationMin: number } | null;
    total: number;
    items: DashboardSlot[];
  };
  urgentActions: DashboardUrgentAction[];
}

const PRIORITY_CLASS: Record<UrgentPriority, string> = {
  high: "border-red-200 bg-red-50 text-red-900",
  medium: "border-amber-200 bg-amber-50 text-amber-900",
  low: "border-gray-200 bg-white text-gray-800",
};

type AppointmentSheetMode = "details" | "complete" | "cancel" | "notify" | "noShow";
type HomeOpenTabOptions = { settingsSection?: HomeSettingsSection | undefined };

export function HomePage({
  token,
  onOpenTab,
  onOpenAppointment,
}: {
  token: string;
  onOpenTab: (tab: HomeTargetTab, options?: HomeOpenTabOptions) => void;
  onOpenAppointment: (target: HomeAppointmentOpenTarget) => void;
}) {
  const fallbackToday = toLocalDateStr(new Date());
  const qc = useQueryClient();
  const [selectedTarget, setSelectedTarget] = useState<HomeAppointmentOpenTarget | null>(null);
  const [selectedFallback, setSelectedFallback] = useState<DashboardAppointment | null>(null);
  const [sheetMode, setSheetMode] = useState<AppointmentSheetMode>("details");
  const [actualPriceInput, setActualPriceInput] = useState("");
  const [returnReminderDaysInput, setReturnReminderDaysInput] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifyResult, setNotifyResult] = useState<"ok" | "no_channel" | null>(null);
  const [isTodayListOpen, setIsTodayListOpen] = useState(false);
  const [isOverdueListOpen, setIsOverdueListOpen] = useState(false);
  const [liveNow, setLiveNow] = useState(() => new Date());

  const { data: dashboard, isLoading, error } = useQuery({
    queryKey: ["master-dashboard"],
    queryFn: () => apiFetch<MasterDashboard>(token, "/api/dashboard/master"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const businessTimeZone = dashboard?.business.timeZone ?? DEFAULT_BUSINESS_TIME_ZONE;
  const businessToday = dashboard?.today.date ?? fallbackToday;

  const { data: selectedDayAppointments = [] } = useQuery({
    queryKey: ["home-appointment-day", selectedTarget?.date],
    queryFn: () => apiFetch<DashboardAppointment[]>(token, `/api/appointments?date=${selectedTarget!.date}`),
    enabled: !!selectedTarget,
    staleTime: 15_000,
  });

  const { data: todayAppointments = [], isFetching: isTodayAppointmentsFetching } = useQuery({
    queryKey: ["home-today-appointments", businessToday],
    queryFn: () => apiFetch<DashboardAppointment[]>(token, `/api/appointments?date=${businessToday}`),
    enabled: !!dashboard?.nextAppointment,
    staleTime: 15_000,
  });

  const selectedAppointment =
    selectedDayAppointments.find((appointment) => appointment.id === selectedTarget?.appointmentId) ??
    selectedFallback;

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      apiFetch(token, `/api/appointments/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      invalidateAppointmentData();
      closeAppointmentSheet();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => apiFetch(token, `/api/appointments/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidateAppointmentData();
      closeAppointmentSheet();
    },
  });

  const notifyMutation = useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) =>
      apiFetch(token, `/api/appointments/${id}/notify-client`, { method: "POST", body: JSON.stringify({ message }) }),
    onSuccess: () => {
      setNotifyResult("ok");
      invalidateAppointmentData();
    },
    onError: () => {
      setNotifyResult("no_channel");
    },
  });

  useEffect(() => {
    if (!selectedAppointment) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closeAppointmentSheet();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedAppointment]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setLiveNow(new Date()), 30_000);
    return () => window.clearInterval(intervalId);
  }, []);

  if (isLoading) {
    return <div className="p-4 py-10 text-center text-gray-400">Загрузка...</div>;
  }

  if (error || !dashboard) {
    return (
      <div className="p-4 py-10 text-center text-red-500">
        Не удалось загрузить главный экран.
      </div>
    );
  }

  const planCard = getPlanAccessCard({
    planStatus: dashboard.business.planStatus,
    planExpiresAt: dashboard.business.planExpiresAt,
    now: new Date(dashboard.generatedAt),
  });
  const primaryAppointmentState = getHomePrimaryAppointmentState(
    dashboard.nextAppointment,
    liveNow,
  );
  const dashboardDateLabel = getHomeDashboardDateLabel({
    todayDate: dashboard.today.date,
    nextAppointment: dashboard.nextAppointment,
    timeZone: businessTimeZone,
  });
  const nonOverdueUrgentActionsCount = dashboard.urgentActions.filter(
    (action) => action.id !== "finish-overdue",
  ).length;
  const attentionCount = nonOverdueUrgentActionsCount + dashboard.unfinishedAppointments.total;
  const hasAttention = attentionCount > 0;
  const otherTodayAppointments = dashboard.nextAppointment
    ? getOtherTodayAppointments(todayAppointments, dashboard.nextAppointment)
    : [];

  function invalidateAppointmentData() {
    void qc.invalidateQueries({ queryKey: ["master-dashboard"] });
    void qc.invalidateQueries({ queryKey: ["today-stats"] });
    void qc.invalidateQueries({ queryKey: ["appointments"] });
    void qc.invalidateQueries({ queryKey: ["home-appointment-day"] });
    void qc.invalidateQueries({ queryKey: ["home-today-appointments"] });
  }

  function openAppointmentSheet(
    appointment: DashboardAppointment,
    mode: AppointmentSheetMode = "details",
  ) {
    setSelectedTarget(getHomeAppointmentOpenTarget(appointment, businessTimeZone));
    setSelectedFallback(appointment);
    setSheetMode(mode);
    setActualPriceInput(getSuggestedPriceRubles(appointment));
    setReturnReminderDaysInput("");
    setNotifyMessage(`Напоминаем о вашей записи в ${fmtTime(appointment.startsAt, businessTimeZone)}. Вы в пути?`);
    setNotifyResult(null);
  }

  function closeAppointmentSheet() {
    setSelectedTarget(null);
    setSelectedFallback(null);
    setSheetMode("details");
    setActualPriceInput("");
    setReturnReminderDaysInput("");
    setNotifyMessage("");
    setNotifyResult(null);
  }

  function startComplete(appointment: DashboardAppointment) {
    setActualPriceInput(getSuggestedPriceRubles(appointment));
    setReturnReminderDaysInput("");
    setSheetMode("complete");
  }

  function confirmComplete(appointment: DashboardAppointment) {
    patchMutation.mutate({
      id: appointment.id,
      body: buildCompleteAppointmentPatch({
        appointment,
        actualPriceRubles: actualPriceInput,
        returnReminderDays: returnReminderDaysInput,
        timeZone: businessTimeZone,
      }),
    });
  }

  function markNoShow(appointment: DashboardAppointment) {
    patchMutation.mutate({ id: appointment.id, body: { status: "NO_SHOW" } });
  }

  return (
    <>
    <div className="mx-auto w-full max-w-2xl min-w-0 space-y-4 p-4">
      <section className="min-w-0 overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Рабочий день</p>
            <h2 className="mt-1 break-words text-2xl font-bold text-gray-900">{primaryAppointmentState.label}</h2>
            <p className="mt-1 text-sm text-gray-500">{dashboardDateLabel}</p>
          </div>
          <button
            type="button"
            onClick={() => onOpenTab("appointments")}
            className="shrink-0 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
          >
            Расписание
          </button>
        </div>

        {dashboard.nextAppointment ? (
          <>
            <AppointmentSummary
              appointment={dashboard.nextAppointment}
              businessTimeZone={businessTimeZone}
              todayDate={dashboard.today.date}
              state={primaryAppointmentState}
              onOpen={() => openAppointmentSheet(dashboard.nextAppointment!)}
            />
            <OtherTodayAppointments
              appointments={otherTodayAppointments}
              businessTimeZone={businessTimeZone}
              expanded={isTodayListOpen}
              isLoading={isTodayAppointmentsFetching && otherTodayAppointments.length === 0}
              onToggle={() => setIsTodayListOpen((value) => !value)}
              onOpenAppointment={openAppointmentSheet}
            />
          </>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-800">Записей на сегодня пока нет</p>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              Можно добавить клиента вручную или оставить день свободным.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onOpenTab("appointments")}
                className="rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-white shadow-sm hover:opacity-90"
              >
                Новая запись
              </button>
              <button
                type="button"
                onClick={() => onOpenTab("appointments")}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                Открыть день
              </button>
            </div>
          </div>
        )}
      </section>

      {hasAttention && (
        <section className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-red-500">Внимание</p>
              <h3 className="mt-1 text-base font-bold text-gray-900">Что нужно обработать</h3>
            </div>
            <span className="rounded-full bg-red-50 px-2 py-1 text-xs font-semibold text-red-600">{attentionCount}</span>
          </div>
          {dashboard.urgentActions.length > 0 && (
            <div className="mt-3 space-y-2">
              {dashboard.urgentActions.map((action) => {
                const presentation = getHomeUrgentActionPresentation(action);

                if (presentation.kind === "overdueAppointments") {
                  return (
                    <OverdueAppointmentsAction
                      key={action.id}
                      action={action}
                      appointments={dashboard.unfinishedAppointments.items}
                      businessTimeZone={businessTimeZone}
                      expanded={isOverdueListOpen}
                      onToggle={() => setIsOverdueListOpen((value) => !value)}
                      onOpenAppointment={openAppointmentSheet}
                      onStartComplete={(appointment) => openAppointmentSheet(appointment, "complete")}
                      onMarkNoShow={(appointment) => openAppointmentSheet(appointment, "noShow")}
                      onNotify={(appointment) => openAppointmentSheet(appointment, "notify")}
                      onCancel={(appointment) => openAppointmentSheet(appointment, "cancel")}
                    />
                  );
                }

                return (
                  <div
                    key={action.id}
                    className={`rounded-2xl border p-3 ${PRIORITY_CLASS[action.priority]}`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold">{action.title}</p>
                        <p className="mt-1 text-xs leading-5 opacity-80">{action.body}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onOpenTab(action.targetTab)}
                        className="shrink-0 rounded-xl bg-white px-3 py-2 text-xs font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
                      >
                        {presentation.buttonLabel}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <section className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base font-bold text-gray-900">Сегодня в цифрах</h3>
          <button
            type="button"
            onClick={() => onOpenTab("appointments")}
            className="text-xs font-semibold text-primary hover:underline"
          >
            Все записи
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Metric label="Активные" value={String(dashboard.today.active)} />
          <Metric label="Закрыто" value={String(dashboard.today.completed)} />
          <Metric label="Окна" value={String(dashboard.freeWindows.total)} />
          <Metric label="Выручка" value={dashboard.today.revenueKopecks > 0 ? fmtPrice(dashboard.today.revenueKopecks) : "—"} />
        </div>
      </section>

      <section className={`min-w-0 rounded-2xl border p-4 shadow-sm ${getPlanSectionClass(planCard.tone)}`}>
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">{planCard.eyebrow}</p>
            <h3 className="mt-1 text-base font-bold text-gray-900">{planCard.title}</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600">{planCard.body}</p>
          </div>
          <button
            type="button"
            onClick={() => onOpenTab(planCard.tone === "danger" ? "settings" : "marketplace")}
            className="shrink-0 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:opacity-90"
          >
            {planCard.buttonLabel}
          </button>
        </div>
      </section>

      <HomeLaunchChecklist
        storageKey={getLaunchChecklistStorageKey(dashboard.business.id)}
        onOpenTab={onOpenTab}
      />
    </div>
    {selectedAppointment && selectedTarget && (
      <HomeAppointmentSheet
        appointment={selectedAppointment}
        businessTimeZone={businessTimeZone}
        mode={sheetMode}
        actualPriceInput={actualPriceInput}
        returnReminderDaysInput={returnReminderDaysInput}
        notifyMessage={notifyMessage}
        notifyResult={notifyResult}
        isPatchPending={patchMutation.isPending}
        isCancelPending={cancelMutation.isPending}
        isNotifyPending={notifyMutation.isPending}
        onClose={closeAppointmentSheet}
        onOpenInCalendar={() => {
          onOpenAppointment(selectedTarget);
          closeAppointmentSheet();
        }}
        onSetMode={setSheetMode}
        onActualPriceInputChange={setActualPriceInput}
        onReturnReminderDaysInputChange={setReturnReminderDaysInput}
        onNotifyMessageChange={setNotifyMessage}
        onStartComplete={() => startComplete(selectedAppointment)}
        onComplete={() => confirmComplete(selectedAppointment)}
        onMarkNoShow={() => markNoShow(selectedAppointment)}
        onCancel={() => cancelMutation.mutate(selectedAppointment.id)}
        onNotify={() => notifyMutation.mutate({ id: selectedAppointment.id, message: notifyMessage })}
      />
    )}
    </>
  );
}

interface HomeLaunchChecklistState {
  checkedIds: LaunchChecklistStepId[];
  expanded: boolean;
  dismissed: boolean;
}

const EMPTY_LAUNCH_CHECKLIST_STATE: HomeLaunchChecklistState = {
  checkedIds: [],
  expanded: false,
  dismissed: false,
};

function HomeLaunchChecklist({
  storageKey,
  onOpenTab,
}: {
  storageKey: string;
  onOpenTab: (tab: HomeTargetTab, options?: HomeOpenTabOptions) => void;
}) {
  const [state, setState] = useState<HomeLaunchChecklistState>(() =>
    readLaunchChecklistState(storageKey),
  );

  useEffect(() => {
    setState(readLaunchChecklistState(storageKey));
  }, [storageKey]);

  if (state.dismissed) return null;

  const progress = getLaunchChecklistProgress(state.checkedIds);
  const checked = new Set(state.checkedIds);

  function updateState(updater: (current: HomeLaunchChecklistState) => HomeLaunchChecklistState) {
    setState((current) => {
      const next = updater(current);
      writeLaunchChecklistState(storageKey, next);
      return next;
    });
  }

  function toggleStep(stepId: LaunchChecklistStepId) {
    updateState((current) => {
      const nextChecked = new Set(current.checkedIds);
      if (nextChecked.has(stepId)) {
        nextChecked.delete(stepId);
      } else {
        nextChecked.add(stepId);
      }

      return {
        ...current,
        checkedIds: LAUNCH_CHECKLIST_STEPS
          .filter((step) => nextChecked.has(step.id))
          .map((step) => step.id),
      };
    });
  }

  return (
    <section className="min-w-0 rounded-2xl border border-emerald-100 bg-[#f8fbf6] p-4 shadow-sm">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
            Настройка кабинета
          </p>
          <h3 className="mt-1 text-base font-bold text-gray-900">
            Чеклист запуска
          </h3>
          <p className="mt-1 text-sm leading-5 text-gray-600">
            Короткая подсказка для первого запуска. Можно свернуть сейчас или скрыть совсем.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              progress.isComplete
                ? "bg-emerald-100 text-emerald-800"
                : "bg-white text-gray-600"
            }`}
          >
            {progress.label}
          </span>
          <button
            type="button"
            onClick={() => updateState((current) => ({ ...current, expanded: !current.expanded }))}
            className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50"
          >
            {state.expanded ? "Свернуть" : "Открыть"}
          </button>
          <button
            type="button"
            onClick={() => updateState((current) => ({ ...current, dismissed: true }))}
            className="rounded-lg px-3 py-2 text-xs font-semibold text-gray-500 hover:bg-white"
          >
            Скрыть
          </button>
        </div>
      </div>

      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white">
        <div
          className="h-full rounded-full bg-emerald-600 transition-all"
          style={{ width: `${progress.percent}%` }}
        />
      </div>

      {state.expanded && (
        <div className="mt-3 space-y-2">
          {LAUNCH_CHECKLIST_STEPS.map((step) => (
            <div
              key={step.id}
              className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-xl border border-emerald-100 bg-white p-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center"
            >
              <input
                type="checkbox"
                checked={checked.has(step.id)}
                onChange={() => toggleStep(step.id)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600 sm:mt-0"
                aria-label={step.title}
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{step.title}</p>
                <p className="mt-0.5 text-xs leading-5 text-gray-500">{step.body}</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  onOpenTab(step.targetTab, {
                    settingsSection: step.settingsSection,
                  })
                }
                className="col-start-2 w-fit rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 sm:col-start-auto"
              >
                {step.buttonLabel}
              </button>
            </div>
          ))}
          <p className="rounded-xl bg-white px-3 py-2 text-xs leading-5 text-gray-500">
            Когда чеклист больше не нужен, нажмите «Скрыть». Блок перестанет показываться на этом устройстве.
          </p>
        </div>
      )}
    </section>
  );
}

function readLaunchChecklistState(storageKey: string): HomeLaunchChecklistState {
  if (typeof window === "undefined") return EMPTY_LAUNCH_CHECKLIST_STATE;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return EMPTY_LAUNCH_CHECKLIST_STATE;
    const parsed = JSON.parse(raw) as Partial<HomeLaunchChecklistState>;

    return {
      checkedIds: LAUNCH_CHECKLIST_STEPS
        .map((step) => step.id)
        .filter((id) => parsed.checkedIds?.includes(id)) as LaunchChecklistStepId[],
      expanded: parsed.expanded === true,
      dismissed: parsed.dismissed === true,
    };
  } catch {
    return EMPTY_LAUNCH_CHECKLIST_STATE;
  }
}

function writeLaunchChecklistState(storageKey: string, state: HomeLaunchChecklistState) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // Local storage can be unavailable in private modes; the checklist still works for the current session.
  }
}

function AppointmentSummary({
  appointment,
  businessTimeZone,
  todayDate,
  state,
  onOpen,
}: {
  appointment: DashboardAppointment;
  businessTimeZone: string;
  todayDate: string;
  state: HomePrimaryAppointmentState;
  onOpen: () => void;
}) {
  const stateClass =
    state.kind === "current"
      ? "border-emerald-200 bg-emerald-50/80"
      : state.kind === "past"
        ? "border-red-200 bg-red-50/80"
      : "border-primary/15 bg-primary/5";
  const stateBadgeClass = state.kind === "past" ? "text-red-600" : "text-primary";
  const statusMeta = getMasterVisibleAppointmentStatusMeta(appointment.status);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => openOnKeyboard(event, onOpen)}
      className={`mt-4 cursor-pointer rounded-2xl border p-4 transition hover:border-primary/25 hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${stateClass}`}
      aria-label={`Открыть запись ${appointment.client.name}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-2xl font-bold tabular-nums leading-tight text-gray-900">
            {getHomePrimaryAppointmentTimeLabel(appointment, todayDate, businessTimeZone)}
          </p>
          <p className="mt-1 truncate text-sm font-semibold text-gray-800">{appointment.service.name}</p>
          <p className="mt-1 text-xs text-gray-500">
            {appointment.client.name} · {appointment.service.durationMin} мин
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className={`rounded-full bg-white px-2 py-1 text-[11px] font-semibold shadow-sm ${stateBadgeClass}`}>
            {state.label}
          </span>
          {statusMeta && (
            <span className={`text-[11px] font-medium ${statusMeta.textClass}`}>
              {statusMeta.label}
            </span>
          )}
        </div>
      </div>
      {appointment.client.phone && (
        <a
          href={`tel:${appointment.client.phone}`}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className="mt-3 inline-block text-sm font-semibold text-primary hover:underline"
        >
          {appointment.client.phone}
        </a>
      )}
    </div>
  );
}

function OverdueAppointmentsAction({
  action,
  appointments,
  businessTimeZone,
  expanded,
  onToggle,
  onOpenAppointment,
  onStartComplete,
  onMarkNoShow,
  onNotify,
  onCancel,
}: {
  action: DashboardUrgentAction;
  appointments: DashboardAppointment[];
  businessTimeZone: string;
  expanded: boolean;
  onToggle: () => void;
  onOpenAppointment: (appointment: DashboardAppointment) => void;
  onStartComplete: (appointment: DashboardAppointment) => void;
  onMarkNoShow: (appointment: DashboardAppointment) => void;
  onNotify: (appointment: DashboardAppointment) => void;
  onCancel: (appointment: DashboardAppointment) => void;
}) {
  const contentId = "home-overdue-appointments";

  return (
    <div className={`overflow-hidden rounded-2xl border ${PRIORITY_CLASS[action.priority]}`}>
      <button
        type="button"
        onClick={onToggle}
        className="group flex w-full items-start justify-between gap-3 p-3 text-left transition hover:bg-white/45 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        aria-expanded={expanded}
        aria-controls={contentId}
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold">{action.title}</span>
          <span className="mt-1 block text-xs leading-5 opacity-80">{action.body}</span>
        </span>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-base font-bold text-red-600 shadow-sm transition group-hover:bg-red-600 group-hover:text-white ${
            expanded ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        >
          ↓
        </span>
      </button>

      {expanded && (
        <div id={contentId} className="border-t border-red-100 bg-white/55 p-2">
          {appointments.length > 0 ? (
            <div className="space-y-2">
              {appointments.map((appointment) => (
                <OverdueAppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  businessTimeZone={businessTimeZone}
                  onOpen={() => onOpenAppointment(appointment)}
                  onStartComplete={() => onStartComplete(appointment)}
                  onMarkNoShow={() => onMarkNoShow(appointment)}
                  onNotify={() => onNotify(appointment)}
                  onCancel={() => onCancel(appointment)}
                />
              ))}
            </div>
          ) : (
            <p className="rounded-xl bg-white px-3 py-2 text-xs text-gray-500">
              Нет записей, которые нужно завершить.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function OverdueAppointmentCard({
  appointment,
  businessTimeZone,
  onOpen,
  onStartComplete,
  onMarkNoShow,
  onNotify,
  onCancel,
}: {
  appointment: DashboardAppointment;
  businessTimeZone: string;
  onOpen: () => void;
  onStartComplete: () => void;
  onMarkNoShow: () => void;
  onNotify: () => void;
  onCancel: () => void;
}) {
  const statusMeta = getMasterVisibleAppointmentStatusMeta(appointment.status);
  const actions = getHomeOverdueAppointmentCardActions(
    getAppointmentActionAvailability(appointment),
  );
  const primaryAction = actions.find((action) => action.prominence === "primary");
  const secondaryActions = actions.filter((action) => action.prominence !== "primary");

  return (
    <div className="rounded-xl border border-red-100 bg-white p-3 shadow-sm transition hover:border-red-200 hover:bg-red-50/45">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label={`Открыть запись ${appointment.client.name}`}
        >
          <span className="block truncate text-sm font-semibold text-gray-900">
            {appointment.client.name} · {appointment.service.name}
          </span>
          <span className="mt-1 block text-xs leading-5 text-red-700">
            {formatShortDateTime(appointment.startsAt, businessTimeZone)} - {fmtTime(appointment.endsAt, businessTimeZone)}
            {statusMeta ? ` · ${statusMeta.label}` : ""}
          </span>
        </button>
        {primaryAction && (
          <button
            type="button"
            onClick={onStartComplete}
            className="min-w-[92px] shrink-0 rounded-xl bg-red-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-red-700"
          >
            {primaryAction.label}
          </button>
        )}
      </div>
      {secondaryActions.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {secondaryActions.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={() => {
                if (action.id === "noShow") onMarkNoShow();
                if (action.id === "notify") onNotify();
                if (action.id === "cancel") onCancel();
              }}
              className={getOverdueSecondaryActionClass(action.prominence)}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getOverdueSecondaryActionClass(
  prominence: "primary" | "secondary" | "dangerSubtle",
): string {
  const base =
    "min-h-9 rounded-lg px-2 py-1.5 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

  if (prominence === "dangerSubtle") {
    return `${base} border border-red-100 bg-white text-red-600 hover:bg-red-50`;
  }

  return `${base} border border-gray-100 bg-gray-50 text-gray-700 hover:bg-white`;
}

function OtherTodayAppointments({
  appointments,
  businessTimeZone,
  expanded,
  isLoading,
  onToggle,
  onOpenAppointment,
}: {
  appointments: DashboardAppointment[];
  businessTimeZone: string;
  expanded: boolean;
  isLoading: boolean;
  onToggle: () => void;
  onOpenAppointment: (appointment: DashboardAppointment) => void;
}) {
  if (appointments.length === 0 && !isLoading) return null;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={onToggle}
        disabled={isLoading}
        className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-left transition hover:border-primary/20 hover:bg-primary/5 disabled:cursor-wait disabled:opacity-70"
        aria-expanded={expanded}
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-gray-800">
            {isLoading ? "Загружаем записи..." : getOtherTodayToggleLabel(appointments.length, expanded)}
          </span>
          {!isLoading && (
            <span className="mt-0.5 block text-xs text-gray-500">
              Быстрый обзор дня без перехода в расписание
            </span>
          )}
        </span>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-base font-bold text-primary shadow-sm transition group-hover:bg-primary group-hover:text-white ${
            expanded ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        >
          ↓
        </span>
      </button>

      {expanded && appointments.length > 0 && (
        <div className="mt-2 overflow-hidden rounded-2xl border border-gray-100 bg-gray-50/80 p-2">
          <div className="space-y-1.5">
            {appointments.map((appointment) => {
              const statusMeta = getMasterVisibleAppointmentStatusMeta(appointment.status);

              return (
                <button
                  key={appointment.id}
                  type="button"
                  onClick={() => onOpenAppointment(appointment)}
                  className="flex w-full min-w-0 items-center gap-3 rounded-xl bg-white px-3 py-2 text-left shadow-sm ring-1 ring-gray-100 transition hover:-translate-y-0.5 hover:ring-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <span className="w-16 shrink-0 text-sm font-bold tabular-nums text-gray-900">
                    {fmtTime(appointment.startsAt, businessTimeZone)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-gray-800">
                      {appointment.service.name}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-gray-500">
                      {appointment.client.name} · {appointment.service.durationMin} мин
                    </span>
                  </span>
                  {statusMeta && (
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${statusMeta.badgeClass}`}>
                      {statusMeta.label}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function HomeAppointmentSheet({
  appointment,
  businessTimeZone,
  mode,
  actualPriceInput,
  returnReminderDaysInput,
  notifyMessage,
  notifyResult,
  isPatchPending,
  isCancelPending,
  isNotifyPending,
  onClose,
  onOpenInCalendar,
  onSetMode,
  onActualPriceInputChange,
  onReturnReminderDaysInputChange,
  onNotifyMessageChange,
  onStartComplete,
  onComplete,
  onMarkNoShow,
  onCancel,
  onNotify,
}: {
  appointment: DashboardAppointment;
  businessTimeZone: string;
  mode: AppointmentSheetMode;
  actualPriceInput: string;
  returnReminderDaysInput: string;
  notifyMessage: string;
  notifyResult: "ok" | "no_channel" | null;
  isPatchPending: boolean;
  isCancelPending: boolean;
  isNotifyPending: boolean;
  onClose: () => void;
  onOpenInCalendar: () => void;
  onSetMode: (mode: AppointmentSheetMode) => void;
  onActualPriceInputChange: (value: string) => void;
  onReturnReminderDaysInputChange: (value: string) => void;
  onNotifyMessageChange: (value: string) => void;
  onStartComplete: () => void;
  onComplete: () => void;
  onMarkNoShow: () => void;
  onCancel: () => void;
  onNotify: () => void;
}) {
  const actionAvailability = getAppointmentActionAvailability(appointment);
  const priceLabel = formatAppointmentPrice(appointment);
  const actionPending = isPatchPending || isCancelPending || isNotifyPending;
  const statusMeta = getMasterVisibleAppointmentStatusMeta(appointment.status);

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-gray-950/35 px-3 sm:items-center"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Запись ${appointment.client.name}`}
        className="max-h-[88dvh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-gray-100 bg-white p-4 shadow-2xl sm:rounded-3xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              Запись
            </p>
            <h3 className="mt-1 text-2xl font-bold tabular-nums text-gray-900">
              {fmtTime(appointment.startsAt, businessTimeZone)} - {fmtTime(appointment.endsAt, businessTimeZone)}
            </h3>
            <p className="mt-1 truncate text-sm font-semibold text-gray-800">
              {appointment.service.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-50 text-xl leading-none text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Закрыть карточку записи"
          >
            ×
          </button>
        </div>

        <div className="mt-4 grid gap-2 rounded-2xl bg-gray-50 p-3 text-sm text-gray-600">
          {statusMeta && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-400">Статус</span>
              <span className={`rounded-full bg-white px-2 py-1 text-xs font-semibold shadow-sm ${statusMeta.textClass}`}>
                {statusMeta.label}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-400">Клиент</span>
            <span className="min-w-0 truncate font-semibold text-gray-800">
              {appointment.client.name}
            </span>
          </div>
          {appointment.client.phone && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-400">Телефон</span>
              <a
                href={`tel:${appointment.client.phone}`}
                className="font-semibold text-primary hover:underline"
              >
                {appointment.client.phone}
              </a>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-400">Услуга</span>
            <span className="text-right font-semibold text-gray-800">
              {appointment.service.durationMin} мин{priceLabel ? ` · ${priceLabel}` : ""}
            </span>
          </div>
          {appointment.notes && (
            <p className="rounded-xl bg-white px-3 py-2 text-xs italic text-gray-500">
              {appointment.notes}
            </p>
          )}
        </div>

        {mode === "details" && (
          <div className="mt-4 space-y-2">
            {(actionAvailability.canComplete ||
              actionAvailability.canContact ||
              actionAvailability.canMarkNoShow ||
              actionAvailability.canCancel) && (
              <div className="grid grid-cols-2 gap-2">
                {actionAvailability.canComplete && (
                  <button
                    type="button"
                    onClick={onStartComplete}
                    className="rounded-xl bg-blue-50 px-3 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                  >
                    Завершить
                  </button>
                )}
                {actionAvailability.canContact && (
                  <button
                    type="button"
                    onClick={() => onSetMode("notify")}
                    className="rounded-xl bg-purple-50 px-3 py-3 text-sm font-semibold text-purple-700 transition hover:bg-purple-100"
                  >
                    Написать клиенту
                  </button>
                )}
                {actionAvailability.canMarkNoShow && (
                  <button
                    type="button"
                    onClick={() => onSetMode("noShow")}
                    disabled={isPatchPending}
                    className="rounded-xl bg-orange-50 px-3 py-3 text-sm font-semibold text-orange-700 transition hover:bg-orange-100 disabled:opacity-50"
                  >
                    Не пришёл
                  </button>
                )}
                {actionAvailability.canCancel && (
                  <button
                    type="button"
                    onClick={() => onSetMode("cancel")}
                    className="rounded-xl bg-red-50 px-3 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                  >
                    Отменить
                  </button>
                )}
              </div>
            )}
            {actionAvailability.canReschedule && !actionAvailability.canComplete && (
              <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-500">
                Завершить или отметить неявку можно будет, когда наступит время записи.
              </p>
            )}
            <button
              type="button"
              onClick={onOpenInCalendar}
              className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              Открыть в записях
            </button>
          </div>
        )}

        {mode === "complete" && (
          <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/40 p-3">
            <p className="text-xs font-semibold text-gray-600">Фактическая оплата</p>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number"
                value={actualPriceInput}
                onChange={(event) => onActualPriceInputChange(event.target.value)}
                placeholder="Сумма в рублях"
                className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary/50 focus:outline-none"
                autoFocus
              />
              <span className="text-sm text-gray-500">₽</span>
            </div>
            <div className="mt-2 flex gap-2">
              {typeof appointment.service.priceFrom === "number" && (
                <button
                  type="button"
                  onClick={() =>
                    onActualPriceInputChange(String(Math.round(appointment.service.priceFrom! / 100)))
                  }
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600"
                >
                  По прайсу
                </button>
              )}
              <button
                type="button"
                onClick={() => onActualPriceInputChange("0")}
                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-500"
              >
                Без оплаты
              </button>
            </div>
            <ReturnReminderDaysPicker
              appointment={appointment}
              businessTimeZone={businessTimeZone}
              daysInput={returnReminderDaysInput}
              onDaysInputChange={onReturnReminderDaysInputChange}
              className="mt-3 border-t border-blue-100 pt-3"
            />
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => onSetMode("details")}
                className="flex-1 rounded-xl border border-gray-200 bg-white py-2 text-sm font-semibold text-gray-500"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={onComplete}
                disabled={actionPending}
                className="flex-1 rounded-xl bg-blue-500 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {isPatchPending ? "..." : "Завершить"}
              </button>
            </div>
          </div>
        )}

        {mode === "notify" && (
          <div className="mt-4 rounded-2xl border border-purple-100 bg-purple-50/40 p-3">
            {notifyResult === "ok" ? (
              <p className="py-2 text-center text-sm font-semibold text-green-600">
                Сообщение отправлено
              </p>
            ) : notifyResult === "no_channel" ? (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-red-500">
                  Нет Telegram/MAX для сообщения.
                </p>
                {appointment.client.phone && (
                  <a
                    href={`tel:${appointment.client.phone}`}
                    className="block rounded-xl bg-white px-3 py-2 text-center text-sm font-semibold text-primary"
                  >
                    {appointment.client.phone}
                  </a>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  {[
                    `Напоминаем о вашей записи в ${fmtTime(appointment.startsAt, businessTimeZone)}. Вы в пути?`,
                    `Ваша запись началась в ${fmtTime(appointment.startsAt, businessTimeZone)}. Всё ещё ждём вас!`,
                    "Всё в силе? Пожалуйста, подтвердите, что придёте.",
                  ].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => onNotifyMessageChange(preset)}
                      className={`w-full rounded-lg border px-2.5 py-1.5 text-left text-xs transition ${
                        notifyMessage === preset
                          ? "border-purple-300 bg-purple-100 text-purple-700"
                          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
                <textarea
                  value={notifyMessage}
                  onChange={(event) => onNotifyMessageChange(event.target.value)}
                  rows={2}
                  className="mt-2 w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-purple-300 focus:outline-none"
                />
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => onSetMode("details")}
                    className="flex-1 rounded-xl border border-gray-200 bg-white py-2 text-sm font-semibold text-gray-500"
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    onClick={onNotify}
                    disabled={isNotifyPending || !notifyMessage.trim()}
                    className="flex-1 rounded-xl bg-purple-500 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {isNotifyPending ? "..." : "Отправить"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {mode === "noShow" && (
          <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50/50 p-3">
            <p className="text-sm text-gray-700">
              Отметить, что {appointment.client.name} не пришёл на {fmtTime(appointment.startsAt, businessTimeZone)}?
            </p>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              Запись уйдёт в отдельный блок неявок. Клиенту не отправляем резкое сообщение автоматически.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => onSetMode("details")}
                className="flex-1 rounded-xl border border-gray-200 bg-white py-2 text-sm font-semibold text-gray-500"
              >
                Нет
              </button>
              <button
                type="button"
                onClick={onMarkNoShow}
                disabled={isPatchPending}
                className="flex-1 rounded-xl bg-orange-500 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {isPatchPending ? "..." : "Да, не пришёл"}
              </button>
            </div>
          </div>
        )}

        {mode === "cancel" && (
          <div className="mt-4 rounded-2xl border border-red-100 bg-red-50/50 p-3">
            <p className="text-sm text-gray-700">
              Отменить запись {appointment.client.name} на {fmtTime(appointment.startsAt, businessTimeZone)}?
            </p>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              Если у клиента подключены уведомления, отправим сообщение об отмене.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => onSetMode("details")}
                className="flex-1 rounded-xl border border-gray-200 bg-white py-2 text-sm font-semibold text-gray-500"
              >
                Нет
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={isCancelPending}
                className="flex-1 rounded-xl bg-red-500 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {isCancelPending ? "..." : "Да, отменить"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function openOnKeyboard(event: React.KeyboardEvent<HTMLElement>, onOpen: () => void) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  onOpen();
}

function Metric({ label, value, dark = false }: { label: string; value: string; dark?: boolean }) {
  return (
    <div className={dark ? "border-t border-white/10 pt-3" : "rounded-2xl bg-white/75 p-3 ring-1 ring-gray-100"}>
      <p className={`text-lg font-bold ${dark ? "text-white" : "text-gray-900"}`}>{value}</p>
      <p className={`mt-1 text-[11px] uppercase tracking-wide ${dark ? "text-white/45" : "text-gray-500"}`}>{label}</p>
    </div>
  );
}

function getSuggestedPriceRubles(appointment: DashboardAppointment): string {
  if (appointment.actualPrice !== null) {
    return String(Math.round(appointment.actualPrice / 100));
  }

  if (typeof appointment.service.priceFrom === "number") {
    return String(Math.round(appointment.service.priceFrom / 100));
  }

  return "";
}

function formatAppointmentPrice(appointment: DashboardAppointment): string {
  if (appointment.actualPrice !== null) return fmtPrice(appointment.actualPrice);
  if (typeof appointment.service.priceFrom !== "number") return "";

  const from = Math.round(appointment.service.priceFrom / 100);
  const to = appointment.service.priceTo ? Math.round(appointment.service.priceTo / 100) : null;
  if (to && to !== from) {
    return `${from.toLocaleString("ru-RU")} - ${to.toLocaleString("ru-RU")} ₽`;
  }

  return `${from.toLocaleString("ru-RU")} ₽`;
}

function getPlanSectionClass(tone: HomePlanCardTone): string {
  if (tone === "danger") return "border-red-200 bg-red-50";
  if (tone === "warning") return "border-amber-200 bg-amber-50";
  if (tone === "success") return "border-emerald-200 bg-emerald-50/70";
  return "border-gray-100 bg-white";
}

function formatShortDateTime(iso: string, timeZone?: string): string {
  return `${formatAppointmentDate(iso, timeZone)} ${fmtTime(iso, timeZone)}`;
}
